import { Control, useFieldArray } from 'react-hook-form';

import { CircleXIcon, Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { QuickQuoteLocationInput } from '../common/QuickQuoteLocationInput';
import {
  BatchQuoteFormValues,
  EMPTY_STOP,
  QuoteCountries,
  QuoteFormStop,
} from '../types';

interface BatchLaneStopsEditorProps {
  index: number;
  control: Control<BatchQuoteFormValues>;
}

/**
 * Multi-stop editor for a single batch lane. Mirrors the single Quick Quote
 * `FTL/StopsSection` (Pickup → intermediate stops → Dropoff → Add Stop) but
 * binds to the batch form path `quotes.${index}.stops`.
 */
export const BatchLaneStopsEditor = ({
  index,
  control,
}: BatchLaneStopsEditorProps) => {
  const { fields, insert, remove } = useFieldArray({
    control,
    name: `quotes.${index}.stops`,
  });

  const handleAddStop = () => {
    // Match the single Quick Quote flow: preserve the dropoff and insert a
    // blank intermediate stop right before it. Otherwise `append` would push
    // the new blank stop past the dropoff, swapping labels in the editor and
    // producing a "pickup → dropoff → blank" route in the collapsed card.
    const insertionIndex = Math.max(fields.length - 1, 1);
    insert(
      insertionIndex,
      {
        ...EMPTY_STOP,
        order: insertionIndex,
        country: QuoteCountries.USA,
      } as QuoteFormStop,
      { shouldFocus: false }
    );

    // Focus the newly inserted stop so the user can start typing immediately,
    // matching single Quick Quote UX.
    setTimeout(() => {
      const locationInput = document.querySelector(
        `input[name="quotes.${index}.stops.${insertionIndex}.location"]`
      ) as HTMLInputElement | null;
      locationInput?.focus();
    }, 100);
  };

  const dropoffIdx = fields.length - 1;

  return (
    <Flex direction='col' gap='xs' className='w-full mx-0'>
      {/* Pickup */}
      {fields.length > 0 && (
        <div className='w-full'>
          <QuickQuoteLocationInput
            name={`quotes.${index}.stops.0.location`}
            label='Pickup'
            placeholder='City, State or ZIP'
            labelClassName='text-xs'
            inputClassName='text-xs tracking-tight bg-neutral-50 border-neutral-500'
            required
          />
        </div>
      )}

      {/* Intermediate stops */}
      {fields.slice(1, -1).map((field, middleIdx) => {
        const stopIdx = middleIdx + 1;
        return (
          <div key={field.id} className='w-full'>
            <Flex
              align='center'
              justify='between'
              gap='xs'
              className='w-full mx-0'
            >
              <div className='flex-1'>
                <QuickQuoteLocationInput
                  name={`quotes.${index}.stops.${stopIdx}.location`}
                  label={`Stop ${middleIdx + 1}`}
                  placeholder='City, State or ZIP'
                  labelClassName='text-xs'
                  inputClassName='text-xs tracking-tight bg-neutral-50 border-neutral-500'
                  required
                />
              </div>
              <button
                type='button'
                aria-label='Remove stop'
                title='Remove stop'
                onClick={() => remove(stopIdx)}
                className='mt-6 flex items-center justify-center rounded bg-transparent p-0 text-neutral-600 hover:text-destructive'
              >
                <CircleXIcon className='w-4 h-4' />
              </button>
            </Flex>
          </div>
        );
      })}

      {/* Dropoff */}
      {dropoffIdx > 0 && (
        <div className='w-full'>
          <QuickQuoteLocationInput
            name={`quotes.${index}.stops.${dropoffIdx}.location`}
            label='Dropoff'
            placeholder='City, State or ZIP'
            labelClassName='text-xs'
            inputClassName='text-xs tracking-tight bg-neutral-50 border-neutral-500'
            required
          />
        </div>
      )}

      <Flex justify='center' className='w-full mt-1'>
        <Button
          buttonNamePosthog={ButtonNamePosthog.QuickQuoteAddStop}
          type='button'
          variant='ghost'
          size='sm'
          onClick={handleAddStop}
          className='w-fit text-xs text-neutral-800 pl-1.5 hover:border-neutral-700 hover:bg-neutral-100 h-7 px-2'
        >
          <Plus className='h-3 w-3 mr-1' />
          Add Stop
        </Button>
      </Flex>
    </Flex>
  );
};
